<script>
import { format, subDays } from "date-fns";
import Chart from "vue-apexcharts";
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import Spinner from "@/components/ui/Spinner";
import statsService from "@/services/stats";
import { INDICATIONS_TYPES } from "@/services/constants";

const DEFAULT_RANGE_DAYS = 6; // last 7 days including today
const MAX_RANGE_DAYS = 15; // matches the backend's enforced cap

const INDICATION_TYPE_LABELS = INDICATIONS_TYPES.reduce((labels, item) => {
  labels[item.type] = item.name;
  return labels;
}, {});

export default {
  name: "Stats",

  components: {
    BaseSubNav,
    Spinner,
    Chart,
  },

  data() {
    const today = format(new Date(), "yyyy-MM-dd");
    const defaultFrom = format(
      subDays(new Date(), DEFAULT_RANGE_DAYS),
      "yyyy-MM-dd"
    );

    return {
      loading: false,
      filters: {
        dateRange: [defaultFrom, today],
      },
      pickerMinDate: null,
      expandedDates: [],
      appointmentStats: [],
      indicationStats: [],
      chartOptions: {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        stroke: { curve: "smooth", width: 2 },
        xaxis: { categories: [] },
        colors: ["#040055", "#67c23a"],
      },
    };
  },

  computed: {
    rangePickerOptions() {
      return {
        disabledDate: (date) => {
          if (date > new Date()) return true;
          if (this.pickerMinDate) {
            const range = MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;
            if (Math.abs(date.getTime() - this.pickerMinDate) > range)
              return true;
          }
          return false;
        },
        onPick: ({ minDate, maxDate }) => {
          this.pickerMinDate = minDate && !maxDate ? minDate.getTime() : null;
        },
      };
    },

    rows() {
      const byDate = new Map();

      this.appointmentStats.forEach((entry) => {
        byDate.set(entry.date, {
          date: entry.date,
          doneCount: entry.doneCount,
          doctorsCount: entry.doctorsCount,
          indicationsTotal: 0,
          indicationsByType: {},
        });
      });

      this.indicationStats.forEach((entry) => {
        const row = byDate.get(entry.date) || {
          date: entry.date,
          doneCount: 0,
          doctorsCount: 0,
          indicationsTotal: 0,
          indicationsByType: {},
        };
        row.indicationsTotal = entry.total;
        row.indicationsByType = entry.byType;
        byDate.set(entry.date, row);
      });

      return [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
    },

    totalDoneCount() {
      return this.appointmentStats.reduce(
        (sum, entry) => sum + entry.doneCount,
        0
      );
    },

    totalIndications() {
      return this.indicationStats.reduce((sum, entry) => sum + entry.total, 0);
    },

    averageDoctorsPerDay() {
      if (!this.appointmentStats.length) return 0;
      const sum = this.appointmentStats.reduce(
        (acc, entry) => acc + entry.doctorsCount,
        0
      );
      return Math.round((sum / this.appointmentStats.length) * 10) / 10;
    },

    series() {
      return [
        {
          name: "Turnos finalizados",
          data: this.rows.map((row) => row.doneCount),
        },
        {
          name: "Indicaciones",
          data: this.rows.map((row) => row.indicationsTotal),
        },
      ];
    },
  },

  watch: {
    "filters.dateRange"() {
      this.expandedDates = [];
      this.load();
    },
  },

  created() {
    this.load();
  },

  methods: {
    buildRange() {
      const [from, to] = this.filters.dateRange;
      // new Date("yyyy-MM-dd") parses as UTC midnight, which in ART (UTC-3)
      // lands on the previous day and cuts today's data out of the range.
      const [fy, fm, fd] = from.split("-").map(Number);
      const [ty, tm, td] = to.split("-").map(Number);
      return {
        from: new Date(fy, fm - 1, fd, 0, 0, 0, 0).toISOString(),
        to: new Date(ty, tm - 1, td, 23, 59, 59, 999).toISOString(),
      };
    },

    async load() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2)
        return;

      this.loading = true;
      const range = this.buildRange();

      try {
        const [appointmentStats, indicationStats] = await Promise.all([
          statsService.getAppointmentStats(range),
          statsService.getIndicationStats(range),
        ]);

        this.appointmentStats = appointmentStats;
        this.indicationStats = indicationStats;

        this.chartOptions = {
          ...this.chartOptions,
          xaxis: { categories: this.rows.map((row) => row.date) },
        };
      } catch (error) {
        console.error("Error loading stats:", error);
      } finally {
        this.loading = false;
      }
    },

    toggleExpanded(date) {
      const index = this.expandedDates.indexOf(date);
      if (index === -1) {
        this.expandedDates.push(date);
      } else {
        this.expandedDates.splice(index, 1);
      }
    },

    isExpanded(date) {
      return this.expandedDates.includes(date);
    },

    typeLabel(type) {
      return INDICATION_TYPE_LABELS[type] || type;
    },

    typeBreakdown(byType) {
      return Object.entries(byType || {}).sort((a, b) => b[1] - a[1]);
    },
  },
};
</script>

<template lang="pug">
div
  BaseSubNav(:showNavigation="false")
    template(#title)
      | Estadísticas

    template(#actions)
      .filters-card
        .filters-card__field
          label.filters-card__label Rango de fechas
          el-date-picker(
            v-model="filters.dateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="-"
            start-placeholder="Desde"
            end-placeholder="Hasta"
            :clearable="false"
            :picker-options="rangePickerOptions"
          )

  .box.box--with-subnav
    .box__content--stretch
      spinner(v-if="loading && !rows.length")

      template(v-else)
        .counters
          .counter-card
            .counter-card__icon
              micon(name="check_circle_outline" width="22" height="22")
            .counter-card__body
              span.counter-card__value {{ totalDoneCount }}
              span.counter-card__label Turnos finalizados
          .counter-card.is-success
            .counter-card__icon
              micon(name="assignment" width="22" height="22")
            .counter-card__body
              span.counter-card__value {{ totalIndications }}
              span.counter-card__label Indicaciones creadas
          .counter-card.is-muted
            .counter-card__icon
              micon(name="people" width="22" height="22")
            .counter-card__body
              span.counter-card__value {{ averageDoctorsPerDay }}
              span.counter-card__label Médicos por día (promedio)

        .chart-card(v-if="rows.length")
          chart(
            width="100%"
            height="280"
            type="line"
            :options="chartOptions"
            :series="series"
          )

        h2.no-results(v-if="!loading && !rows.length") No hay datos para el rango seleccionado

        table(v-if="rows.length")
          thead
            tr
              th
              th Fecha
              th Turnos finalizados
              th Médicos
              th Indicaciones
          tbody
            template(v-for="row in rows")
              tr.row--clickable(:key="row.date" @click="toggleExpanded(row.date)")
                td
                  micon.expand-icon(
                    name="expand_more"
                    :class="{ 'expand-icon--open': isExpanded(row.date) }"
                  )
                td {{ row.date }}
                td {{ row.doneCount }}
                td {{ row.doctorsCount }}
                td {{ row.indicationsTotal }}
              tr.row--detail(v-if="isExpanded(row.date)" :key="`${row.date}-detail`")
                td(colspan="5")
                  .indication-breakdown(v-if="row.indicationsTotal")
                    span.indication-breakdown__item(
                      v-for="[type, count] in typeBreakdown(row.indicationsByType)"
                      :key="type"
                    )
                      | {{ typeLabel(type) }}: {{ count }}
                  span.indication-breakdown__empty(v-else) Sin indicaciones
</template>

<style lang="scss" scoped>
.box--with-subnav {
  min-height: 100px;
}

.box__content--stretch {
  padding-top: 1.25rem;
}

.no-results {
  text-align: center;
  padding: 50px;
  font-size: 1.5em;
  width: 100%;
}

.counters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.counter-card {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  background: $white;
  border-radius: 10px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  padding: 0.9rem 1.4rem;
  min-width: 190px;
  flex: 1 1 190px;
}

.counter-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 50%;
  background-color: $solitude;

  .micon {
    fill: $primary;
  }
}

.counter-card.is-success .counter-card__icon {
  background-color: rgba(103, 194, 58, 0.14);

  .micon {
    fill: #67c23a;
  }
}

.counter-card.is-muted .counter-card__icon {
  background-color: $whisper;

  .micon {
    fill: $lynch;
  }
}

.counter-card__body {
  display: flex;
  flex-direction: column;
}

.counter-card__value {
  font-size: 1.6rem;
  font-weight: bold;
  color: $primary-alt;
  line-height: 1.1;
}

.counter-card__label {
  font-size: 0.8rem;
  color: $lynch;
}

.filters-card {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
}

.filters-card__field {
  display: flex;
  flex-direction: column;
}

.filters-card__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: $lynch;
  margin-bottom: 0.3rem;
}

.chart-card {
  background: $white;
  border-radius: 10px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  padding: 1rem;
  margin-bottom: 1.25rem;
}

table {
  width: 100%;
}

.row--clickable {
  cursor: pointer;

  &:hover {
    background: $alabaster;
  }
}

.expand-icon {
  transition: transform 0.15s ease;

  &--open {
    transform: rotate(180deg);
  }
}

.row--detail td {
  background: $alabaster;
  padding: 0.75rem 1rem;
}

.indication-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.indication-breakdown__item {
  background: $white;
  border-radius: 6px;
  padding: 0.3rem 0.7rem;
  font-size: 0.85rem;
  color: $primary-alt;
  box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.1);
}

.indication-breakdown__empty {
  font-size: 0.85rem;
  color: $lynch;
}
</style>
